import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { CouponStatusUtility } from '../../coupon/coupon-status.utility'
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { combineLatest, Observable, Subject, Subscription } from 'rxjs'
import { BatmanDataService } from '../../landing-page/batman-data.service'
import { DeviceService } from '../../../singleton-services/device.service'
import { SiteIds } from '../../../shared/models/site.model'
import { CheckoutNavigationService } from '../../../shared/services/checkout-navigation.service'
import { ILandingPageComponent } from '../../landing-page/components/landing-page-legacy/landing-page-legacy.component'

export interface IPartnerLogo {
  mobileLogo: string
  desktopLogo: string
  backgroundImage: {
    'background-image': string
  }
}

/**
 * @deprecated We now have a new batman hero so this component is now deprecated
 * @see LandingHeroComponent
 */
@Component({
  selector: 'flo-batman-hero',
  templateUrl: './batman-hero.component.html',
  styleUrls: ['./batman-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatmanHeroComponent implements OnDestroy {
  public constructor(
    private readonly ar: ActivatedRoute,
    private readonly couponStatusUtility: CouponStatusUtility,
    private readonly batmanDataService: BatmanDataService,
    private readonly deviceService: DeviceService,
    private readonly checkoutNavigationService: CheckoutNavigationService
  ) {}

  @Input()
  landingPageProps: ILandingPageComponent
  @Input()
  analyticsProps: any

  get isTrackWrestling() {
    return this.landingPageProps.siteSettings.site_id === SiteIds.TRACK_WRESTLING
  }

  get isWrestling() {
    return this.landingPageProps.siteSettings.site_id === SiteIds.FLOWRESTLING
  }

  get isRacing() {
    return this.landingPageProps.siteSettings.site_id === SiteIds.FLORACING
  }

  get isCollege() {
    return this.landingPageProps.siteSettings.site_id === SiteIds.FLOCOLLEGE
  }

  get isGrappling() {
    return this.landingPageProps.siteSettings.site_id === SiteIds.FLOGRAPPLING
  }

  private readonly ngOnDestroy$ = new Subject<void>()

  public readonly heroData$ = this.batmanDataService.heroData$.pipe(shareReplay(1))

  public readonly signupOrChangeOffer$ = new Subject<void>()

  public readonly showHighlightVideo$: Observable<boolean | undefined> = combineLatest([
    this.heroData$,
    this.deviceService.isDesktop$
  ]).pipe(
    map(([heroData, isDesktop]) => {
      return !!heroData.videoUrl && isDesktop
    })
  )

  public readonly heroSectionStyle$: Observable<Record<string, string>> = this.heroData$.pipe(
    map((heroData): Record<string, string> => ({ 'background-image': `url(${heroData.imageUrl})` })),
    shareReplay()
  )

  public readonly partnerLogos$: Observable<IPartnerLogo> = combineLatest([
    this.heroData$,
    this.deviceService.isDesktop$
  ]).pipe(
    take(1),
    map(([heroData, isDesktop]) => {
      const partnerLogoUri = isDesktop ? heroData.logos : heroData.mobileLogos
      return {
        mobileLogo: heroData.mobileLogos,
        desktopLogo: heroData.logos,
        backgroundImage: { 'background-image': `url(${partnerLogoUri})` }
      }
    })
  )

  public goToSignupOrChangeOffer: Subscription = this.signupOrChangeOffer$
    .pipe(
      tap(() => this.couponStatusUtility.hideCouponErrorSource.next(true)),
      switchMap(() => combineLatest(this.ar.queryParams)),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([queryParams]) => {
      this.checkoutNavigationService.navigateAfterLandingPage(
        queryParams,
        false,
        this.landingPageProps.liveEventRedirect
      )
    })

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
